import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Check, Shield, Target, Zap, Users, Clock, Car } from 'lucide-react'
import { features } from '../../data/siteData'

const featureIcons = [Shield, Target, Users, Clock, Zap, Car]

export default function About() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="about" className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      {/* Top Separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            O nas
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            DLACZEGO MY?
          </h2>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => {
            const Icon = featureIcons[i] || Check
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative p-6 bg-steel-light/50 border border-military/20 rounded-sm hover:border-military/50 hover:bg-steel-light transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-military/10 border border-military/30 rounded-sm group-hover:bg-military/20 transition-colors">
                    <Icon size={20} className="text-military-light" />
                  </div>
                  <p className="font-body text-sm text-chrome-light leading-relaxed pt-2">
                    {feature}
                  </p>
                </div>
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-military-light group-hover:w-full transition-all duration-500" />
              </motion.div>
            )
          })}
        </div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-military/30" />
            <CrosshairIcon />
            <div className="w-12 h-px bg-military/30" />
          </div>
          <blockquote className="font-subheading text-xl sm:text-2xl text-sand italic max-w-3xl mx-auto leading-relaxed">
            „Nie musisz być żołnierzem, żeby poczuć się jak jeden."
          </blockquote>
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className="w-12 h-px bg-military/30" />
            <CrosshairIcon />
            <div className="w-12 h-px bg-military/30" />
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function CrosshairIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-military-light">
      <path d="M6 0V5M6 7V12M0 6H5M7 6H12" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}
